import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, combineLatest, debounceTime, distinctUntilChanged, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { IAActiveActions } from '../models/ia-active-actions.model';
import { IAGenerationOutcome, IACopyFeedbackRequest } from '../models/ia-generation-outcome.model';
import { v4 as uuidv4 } from 'uuid';
import { DEFAULT_PAGING } from 'src/app/@shared/constants/site.constants';
import { environment } from 'src/environments/environment';
import { OrganizationService } from 'src/app/@shared';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private integrationSubject = new BehaviorSubject<any>(null);

  public integrations$ = this.integrationSubject.asObservable();
  constructor(
    private organizationService: OrganizationService,
    private httpClient: HttpClient
  ) { }


  addIntegrations(data: any) {
    this.integrationSubject.next(data);
  }

  getIntegrations() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Integrations/GetAllIntegrations`;

    return this.httpClient.get(url).pipe(
      map((res: any) => res.value),
      catchError(() => {
        return [] as any;
      })
    );
  }

  getIntegration(id: string): Observable<any> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Integrations/${id}`;
    return this.httpClient.get<any>(url);
  }

  // ---- IA providers (Manage Providers overlay) ----

  // Lists every IAProvider (JAI + CEIA fine-tuned/frontier/local) with its IsEnabled flag and
  // Config JSON (which carries SupportedActions + connection data).
  getIAProviders(): Observable<any> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IAProviders/GetIAProviders`;
    return this.httpClient.get(url).pipe(
      map((res: any) => res.value ?? res),
      catchError(() => [] as any)
    );
  }

  getActiveActions(): Observable<IAActiveActions> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/GetActiveActions`;
    return this.httpClient.get<IAActiveActions>(url).pipe(
      catchError(() => of({ IAEnabled: false, CopyEnabled: false, MontageEnabled: false }))
    );
  }

  generateCopy(offerId: string): Observable<IAGenerationOutcome> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/GenerateCopy`;
    const params = new HttpParams().set('key', offerId);
    return this.httpClient.post<IAGenerationOutcome>(url, {}, { params });
  }

  refreshGeneration(requestId: string): Observable<IAGenerationOutcome> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/RefreshGeneration`;
    const params = new HttpParams().set('requestId', requestId);
    return this.httpClient.get<IAGenerationOutcome>(url, { params });
  }

  submitFeedback(responseId: string, body: IACopyFeedbackRequest): Observable<unknown> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/SubmitFeedback`;
    const params = new HttpParams().set('responseId', responseId);
    return this.httpClient.post(url, body, { params });
  }

  completeGeneration(requestId: string, responseId: string): Observable<unknown> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/CompleteGeneration`;
    const params = new HttpParams()
      .set('requestId', requestId)
      .set('responseId', responseId);
    return this.httpClient.post(url, null, { params });
  }

  regenerateCopyResponse(
    requestId: string,
    responseId: string,
    body: IACopyFeedbackRequest
  ): Observable<IAGenerationOutcome> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/RegenerateCopyResponse`;
    const params = new HttpParams()
      .set('requestId', requestId)
      .set('responseId', responseId);
    return this.httpClient.post<IAGenerationOutcome>(url, body, { params });
  }

  regenerateAllCopyResponses(
    requestId: string,
    body: IACopyFeedbackRequest
  ): Observable<IAGenerationOutcome> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IA/RegenerateAllCopyResponses`;
    const params = new HttpParams().set('requestId', requestId);
    return this.httpClient.post<IAGenerationOutcome>(url, body, { params });
  }

  // Updates a provider in place (IsEnabled + Config). The full provider vertex is re-validated server-side.
  updateIAProvider(provider: any, id: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/IAProviders/UpdateIAProvider?key=${id}`;
    return this.httpClient.put(url, provider);
  }

  getIntegrationSearch(clientKey: string, subscription: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Integrations/GetIntegrationSearch?clientkey=${clientKey}&subscription=${subscription}`;
    return this.httpClient.get<any>(url);
  }

  updateIntegration(integration: any, id: string) {
    if (id) {
      let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Integrations/UpdateIntegration?key=${id}`;
      return this.httpClient.put(url, integration);
    } else {
      let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Integrations/SaveIntegration`;
      return this.httpClient.post(url, integration);
    }
  }
}
